import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Clock, Play, Square, Plus, Loader2, Timer, FileSpreadsheet, Calendar, Users, ChevronRight, Trash2, Edit, StopCircle, PlayCircle, Check, X } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_EVENT_TYPES = [
  { value: 'reunion', label: 'Réunion', color: 'bg-blue-100 text-blue-700' },
  { value: 'formation', label: 'Formation', color: 'bg-purple-100 text-purple-700' },
  { value: 'projet', label: 'Projet', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'tache', label: 'Tâche spécifique', color: 'bg-amber-100 text-amber-700' },
  { value: 'autre', label: 'Autre', color: 'bg-slate-100 text-slate-700' }
];

const TYPE_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
  'bg-orange-100 text-orange-700',
  'bg-slate-100 text-slate-700'
];

export default function TimeTrackingPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [activeTab, setActiveTab] = useState('events');
  const [timeEntries, setTimeEntries] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockedIn, setClockedIn] = useState(false);
  
  // Event types (modifiable list)
  const [eventTypes, setEventTypes] = useState(() => {
    const saved = localStorage.getItem('customEventTypes');
    return saved ? JSON.parse(saved) : DEFAULT_EVENT_TYPES;
  });
  const [newTypeName, setNewTypeName] = useState('');
  const [selectedColor, setSelectedColor] = useState(TYPE_COLORS[0]);
  const [showAddType, setShowAddType] = useState(false);
  
  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);
  
  const [submitting, setSubmitting] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventLogs, setEventLogs] = useState([]);
  
  // Forms
  const [tsForm, setTsForm] = useState({ date: format(new Date(), 'yyyy-MM-dd'), project: '', task: '', hours: '', description: '' });
  const [eventForm, setEventForm] = useState({
    name: '',
    description: '',
    event_type: 'autre',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: '',
    assigned_to: [],
    assign_all: false,
    tasks: [],
    notes: ''
  });
  const [logForm, setLogForm] = useState({ hours: '', notes: '' });
  const [newTask, setNewTask] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [entriesRes, tsRes, eventsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/time-entries`),
        axios.get(`${API_URL}/api/timesheets`),
        axios.get(`${API_URL}/api/time-events`),
        axios.get(`${API_URL}/api/time-events/stats/summary`)
      ]);
      setTimeEntries(entriesRes.data);
      setTimesheets(tsRes.data);
      setEvents(eventsRes.data);
      setSummary(summaryRes.data);
      
      const lastEntry = entriesRes.data[0];
      setClockedIn(lastEntry?.entry_type === 'clock_in');
      
      if (isAdmin) {
        const usersRes = await axios.get(`${API_URL}/api/users`);
        setUsers(usersRes.data.filter(u => u.status === 'active'));
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClock = async (type) => {
    try {
      await axios.post(`${API_URL}/api/time-entries`, { entry_type: type });
      toast.success(type === 'clock_in' ? 'Pointage entrée enregistré' : 'Pointage sortie enregistré');
      setClockedIn(type === 'clock_in');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du pointage');
    }
  };

  const handleCreateTimesheet = async (e) => {
    e.preventDefault();
    if (!tsForm.project || !tsForm.task || !tsForm.hours) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/timesheets`, { ...tsForm, hours: parseFloat(tsForm.hours) });
      toast.success('Feuille de temps ajoutée');
      setDialogOpen(false);
      setTsForm({ date: format(new Date(), 'yyyy-MM-dd'), project: '', task: '', hours: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.name || !eventForm.start_date) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/time-events`, eventForm);
      toast.success('Événement créé');
      setEventDialogOpen(false);
      setEventForm({
        name: '',
        description: '',
        event_type: 'autre',
        start_date: format(new Date(), 'yyyy-MM-dd'),
        end_date: '',
        assigned_to: [],
        assign_all: false,
        tasks: [],
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    try {
      await axios.delete(`${API_URL}/api/time-events/${eventId}`);
      toast.success('Événement supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleViewEvent = async (event) => {
    setSelectedEvent(event);
    setEventDetailOpen(true);
    try {
      const res = await axios.get(`${API_URL}/api/time-events/${event.id}/logs`);
      setEventLogs(res.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const handleLogTime = async (action) => {
    if (!selectedEvent) return;
    
    if (action === 'manual' && !logForm.hours) {
      toast.error('Veuillez entrer les heures');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/time-events/${selectedEvent.id}/logs`, {
        event_id: selectedEvent.id,
        hours: action === 'manual' ? parseFloat(logForm.hours) : null,
        action: action,
        notes: logForm.notes
      });
      
      if (action === 'start') {
        toast.success('Chronomètre démarré');
      } else if (action === 'stop') {
        toast.success('Chronomètre arrêté, temps enregistré');
      } else {
        toast.success('Temps enregistré');
        setLogDialogOpen(false);
        setLogForm({ hours: '', notes: '' });
      }
      
      // Refresh logs and summary
      const [logsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/time-events/${selectedEvent.id}/logs`),
        axios.get(`${API_URL}/api/time-events/stats/summary`)
      ]);
      setEventLogs(logsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const addTaskToEvent = () => {
    if (newTask.trim()) {
      setEventForm({ ...eventForm, tasks: [...eventForm.tasks, newTask.trim()] });
      setNewTask('');
    }
  };

  const removeTaskFromEvent = (index) => {
    setEventForm({ ...eventForm, tasks: eventForm.tasks.filter((_, i) => i !== index) });
  };

  const toggleUserSelection = (userId) => {
    if (eventForm.assigned_to.includes(userId)) {
      setEventForm({ ...eventForm, assigned_to: eventForm.assigned_to.filter(id => id !== userId) });
    } else {
      setEventForm({ ...eventForm, assigned_to: [...eventForm.assigned_to, userId] });
    }
  };

  const getTotalHoursToday = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return timesheets.filter(ts => ts.date === today).reduce((sum, ts) => sum + ts.hours, 0);
  };

  const getEventTypeStyle = (type) => {
    return eventTypes.find(t => t.value === type)?.color || 'bg-slate-100 text-slate-700';
  };

  const getEventTypeLabel = (type) => {
    return eventTypes.find(t => t.value === type)?.label || type || 'Autre';
  };

  const addEventType = () => {
    if (newTypeName.trim()) {
      const value = newTypeName.trim().toLowerCase().replace(/\s+/g, '_');
      if (eventTypes.some(t => t.value === value)) {
        toast.error('Ce type existe déjà');
        return;
      }
      const newType = {
        value,
        label: newTypeName.trim(),
        color: selectedColor
      };
      const updatedTypes = [...eventTypes, newType];
      setEventTypes(updatedTypes);
      localStorage.setItem('customEventTypes', JSON.stringify(updatedTypes));
      setEventForm({ ...eventForm, event_type: value });
      setNewTypeName('');
      setSelectedColor(TYPE_COLORS[0]);
      setShowAddType(false);
      toast.success('Type ajouté');
    }
  };

  const removeEventType = (typeValue) => {
    if (DEFAULT_EVENT_TYPES.some(t => t.value === typeValue)) {
      toast.error('Impossible de supprimer un type par défaut');
      return;
    }
    const updatedTypes = eventTypes.filter(t => t.value !== typeValue);
    setEventTypes(updatedTypes);
    localStorage.setItem('customEventTypes', JSON.stringify(updatedTypes));
    toast.success('Type supprimé');
  };

  const getTotalEventHours = (eventId) => {
    return eventLogs
      .filter(log => log.event_id === eventId && ['manual', 'stop'].includes(log.action))
      .reduce((sum, log) => sum + (log.hours || 0), 0)
      .toFixed(2);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="time-tracking-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Suivi du temps</h1>
          <p className="text-slate-500 mt-1">Événements, pointage et feuilles de temps</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button onClick={() => setEventDialogOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-event-btn">
              <Plus className="w-4 h-4 mr-2" />Ajouter un événement
            </Button>
          )}
          <Button onClick={() => setDialogOpen(true)} variant="outline" data-testid="add-timesheet-btn">
            <Plus className="w-4 h-4 mr-2" />Ajouter des heures
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-300 text-sm">Total heures</p>
                <p className="text-3xl font-bold mt-1">{summary?.total_hours || 0}h</p>
              </div>
              <Clock className="w-10 h-10 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Événements</p>
                <p className="text-3xl font-bold mt-1 text-slate-900">{summary?.total_events || 0}</p>
              </div>
              <Calendar className="w-10 h-10 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Heures aujourd'hui</p>
                <p className="text-3xl font-bold mt-1 text-slate-900">{getTotalHoursToday()}h</p>
              </div>
              <Timer className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className={summary?.has_active_timer ? 'border-2 border-emerald-500' : ''}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Chronomètre</p>
                <p className="text-lg font-bold mt-1 text-slate-900">
                  {summary?.has_active_timer ? 'En cours' : 'Arrêté'}
                </p>
              </div>
              {summary?.has_active_timer ? (
                <PlayCircle className="w-10 h-10 text-emerald-500 animate-pulse" />
              ) : (
                <StopCircle className="w-10 h-10 text-slate-300" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Clock In/Out Card */}
      <Card className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <p className="text-blue-200 text-sm">Pointage</p>
              <p className="text-3xl font-bold mt-1">{format(new Date(), 'HH:mm')}</p>
              <p className="text-blue-200 mt-1">{format(new Date(), 'EEEE d MMMM', { locale: fr })}</p>
            </div>
            
            <div className="flex gap-2">
              <Button
                size="lg"
                onClick={() => handleClock('clock_in')}
                disabled={clockedIn}
                className={clockedIn ? 'bg-blue-400/50' : 'bg-emerald-500 hover:bg-emerald-600'}
                data-testid="clock-in-btn"
              >
                <Play className="w-5 h-5 mr-2" />Entrée
              </Button>
              <Button
                size="lg"
                onClick={() => handleClock('clock_out')}
                disabled={!clockedIn}
                className={!clockedIn ? 'bg-blue-400/50' : 'bg-red-500 hover:bg-red-600'}
                data-testid="clock-out-btn"
              >
                <Square className="w-5 h-5 mr-2" />Sortie
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="events" data-testid="events-tab"><Calendar className="w-4 h-4 mr-2" />Feuille des événements</TabsTrigger>
          <TabsTrigger value="clock"><Timer className="w-4 h-4 mr-2" />Pointages</TabsTrigger>
          <TabsTrigger value="timesheet"><FileSpreadsheet className="w-4 h-4 mr-2" />Feuilles de temps</TabsTrigger>
        </TabsList>

        {/* Events Tab */}
        <TabsContent value="events" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Feuille des événements
              </CardTitle>
            </CardHeader>
            <CardContent>
              {events.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Calendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Aucun événement</p>
                  {isAdmin && <p className="text-sm mt-2">Créez votre premier événement avec le bouton ci-dessus</p>}
                </div>
              ) : (
                <div className="space-y-3">
                  {events.map((event) => (
                    <div 
                      key={event.id} 
                      className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                      onClick={() => handleViewEvent(event)}
                      data-testid={`event-item-${event.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-slate-900">{event.name}</h3>
                          <Badge className={getEventTypeStyle(event.event_type)}>{getEventTypeLabel(event.event_type)}</Badge>
                          {event.assign_all && <Badge variant="outline" className="text-xs">Tous les utilisateurs</Badge>}
                        </div>
                        {event.description && <p className="text-sm text-slate-500 mt-1 line-clamp-1">{event.description}</p>}
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(event.start_date), 'dd/MM/yyyy')}
                            {event.end_date && ` - ${format(new Date(event.end_date), 'dd/MM/yyyy')}`}
                          </span>
                          {!event.assign_all && event.assigned_to_names?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {event.assigned_to_names.length} assigné(s)
                            </span>
                          )}
                          {event.tasks?.length > 0 && (
                            <span>{event.tasks.length} tâche(s)</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event.id); }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clock Tab */}
        <TabsContent value="clock" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Historique des pointages</CardTitle>
            </CardHeader>
            <CardContent>
              {timeEntries.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucun pointage enregistré</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {timeEntries.slice(0, 20).map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${entry.entry_type === 'clock_in' ? 'bg-emerald-100' : 'bg-red-100'}`}>
                          {entry.entry_type === 'clock_in' ? (
                            <Play className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <Square className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{entry.entry_type === 'clock_in' ? 'Entrée' : 'Sortie'}</p>
                          <p className="text-sm text-slate-500">{format(new Date(entry.timestamp), 'dd/MM/yyyy HH:mm', { locale: fr })}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timesheet Tab */}
        <TabsContent value="timesheet" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Feuilles de temps</CardTitle>
            </CardHeader>
            <CardContent>
              {timesheets.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune feuille de temps</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {timesheets.map((ts) => (
                    <div key={ts.id} className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{ts.project}</h3>
                          <Badge variant="secondary">{ts.hours}h</Badge>
                          <Badge className={ts.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : ts.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                            {ts.status === 'approved' ? 'Approuvé' : ts.status === 'rejected' ? 'Rejeté' : 'En attente'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500">{ts.task}</p>
                        <p className="text-xs text-slate-400 mt-1">{format(new Date(ts.date), 'dd MMMM yyyy', { locale: fr })}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Event Dialog */}
      <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Créer un événement</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateEvent} className="space-y-4">
            <div className="space-y-2">
              <Label>Nom de l'événement *</Label>
              <Input 
                value={eventForm.name} 
                onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })} 
                placeholder="Ex: Réunion d'équipe hebdomadaire"
                data-testid="event-name-input"
              />
            </div>
            
            {/* Type d'événement - Modifiable */}
            <div className="space-y-2">
              <Label>Type d'événement</Label>
              {!showAddType ? (
                <div className="flex gap-2">
                  <Select value={eventForm.event_type} onValueChange={(v) => setEventForm({ ...eventForm, event_type: v })}>
                    <SelectTrigger data-testid="event-type-select" className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {eventTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <span className={`w-3 h-3 rounded-full ${type.color.split(' ')[0]}`}></span>
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" size="icon" onClick={() => setShowAddType(true)} title="Ajouter un type">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input 
                      value={newTypeName}
                      onChange={(e) => setNewTypeName(e.target.value)}
                      placeholder="Nouveau type..."
                      className="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addEventType();
                        }
                      }}
                    />
                    <Button type="button" variant="outline" size="icon" onClick={addEventType} className="text-emerald-600">
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button type="button" variant="outline" size="icon" onClick={() => { setShowAddType(false); setNewTypeName(''); setSelectedColor(TYPE_COLORS[0]); }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <span className="text-xs text-slate-500 mr-2">Couleur:</span>
                    {TYPE_COLORS.map((color, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`w-6 h-6 rounded-full ${color.split(' ')[0]} border-2 ${selectedColor === color ? 'border-slate-900 scale-110' : 'border-transparent'} transition-transform`}
                        onClick={() => setSelectedColor(color)}
                        title={`Couleur ${i + 1}`}
                      />
                    ))}
                  </div>
                </div>
              )}
              {eventTypes.length > DEFAULT_EVENT_TYPES.length && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {eventTypes.filter(t => !DEFAULT_EVENT_TYPES.some(d => d.value === t.value)).map(type => (
                    <Badge key={type.value} variant="outline" className={`text-xs cursor-pointer hover:bg-red-50 ${type.color}`} onClick={() => removeEventType(type.value)}>
                      {type.label} <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Dates sur la même ligne */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de début *</Label>
                <Input 
                  type="date" 
                  value={eventForm.start_date} 
                  onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })}
                  data-testid="event-start-date"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Date de fin</Label>
                <Input 
                  type="date" 
                  value={eventForm.end_date} 
                  onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                  data-testid="event-end-date"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={eventForm.description} 
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} 
                rows={2}
                placeholder="Description de l'événement..."
              />
            </div>
            
            {/* User Assignment */}
            <div className="space-y-3">
              <Label>Assigner à</Label>
              <div className="flex items-center gap-2 mb-2">
                <Checkbox 
                  id="assign-all" 
                  checked={eventForm.assign_all}
                  onCheckedChange={(checked) => setEventForm({ ...eventForm, assign_all: checked, assigned_to: [] })}
                />
                <label htmlFor="assign-all" className="text-sm font-medium cursor-pointer">Tous les utilisateurs</label>
              </div>
              
              {!eventForm.assign_all && (
                <div className="border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                  {users.map(u => (
                    <div key={u.id} className="flex items-center gap-2">
                      <Checkbox 
                        id={`user-${u.id}`}
                        checked={eventForm.assigned_to.includes(u.id)}
                        onCheckedChange={() => toggleUserSelection(u.id)}
                      />
                      <label htmlFor={`user-${u.id}`} className="text-sm cursor-pointer">
                        {u.first_name} {u.last_name}
                        <span className="text-slate-400 ml-2">({u.department || 'N/A'})</span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
              {eventForm.assigned_to.length > 0 && !eventForm.assign_all && (
                <p className="text-xs text-slate-500">{eventForm.assigned_to.length} utilisateur(s) sélectionné(s)</p>
              )}
            </div>
            
            {/* Tasks */}
            <div className="space-y-2">
              <Label>Tâches</Label>
              <div className="flex gap-2">
                <Input 
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  placeholder="Ajouter une tâche..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTaskToEvent())}
                />
                <Button type="button" variant="outline" onClick={addTaskToEvent}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {eventForm.tasks.length > 0 && (
                <div className="space-y-1 mt-2">
                  {eventForm.tasks.map((task, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded">
                      <span className="text-sm">{task}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeTaskFromEvent(i)}>
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={eventForm.notes} 
                onChange={(e) => setEventForm({ ...eventForm, notes: e.target.value })} 
                rows={2}
                placeholder="Notes additionnelles..."
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEventDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-event-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer l\'événement'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Timesheet Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ajouter des heures</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateTimesheet} className="space-y-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={tsForm.date} onChange={(e) => setTsForm({ ...tsForm, date: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Projet *</Label>
                <Input value={tsForm.project} onChange={(e) => setTsForm({ ...tsForm, project: e.target.value })} placeholder="Nom du projet" />
              </div>
              <div className="space-y-2">
                <Label>Heures *</Label>
                <Input type="number" step="0.5" value={tsForm.hours} onChange={(e) => setTsForm({ ...tsForm, hours: e.target.value })} placeholder="8" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tâche *</Label>
              <Input value={tsForm.task} onChange={(e) => setTsForm({ ...tsForm, task: e.target.value })} placeholder="Description de la tâche" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={tsForm.description} onChange={(e) => setTsForm({ ...tsForm, description: e.target.value })} rows={3} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Event Detail Dialog */}
      <Dialog open={eventDetailOpen} onOpenChange={setEventDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEvent?.name}
              <Badge className={getEventTypeStyle(selectedEvent?.event_type)}>{getEventTypeLabel(selectedEvent?.event_type)}</Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-6">
              {/* Event Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Date</p>
                  <p className="font-medium">
                    {format(new Date(selectedEvent.start_date), 'dd MMMM yyyy', { locale: fr })}
                    {selectedEvent.end_date && ` - ${format(new Date(selectedEvent.end_date), 'dd MMMM yyyy', { locale: fr })}`}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Créé par</p>
                  <p className="font-medium">{selectedEvent.created_by_name}</p>
                </div>
              </div>
              
              {selectedEvent.description && (
                <div>
                  <p className="text-slate-500 text-sm">Description</p>
                  <p className="mt-1">{selectedEvent.description}</p>
                </div>
              )}
              
              {/* Assigned Users */}
              <div>
                <p className="text-slate-500 text-sm mb-2">Assigné à</p>
                {selectedEvent.assign_all ? (
                  <Badge variant="outline">Tous les utilisateurs</Badge>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {selectedEvent.assigned_to_names?.map((name, i) => (
                      <Badge key={i} variant="secondary">{name}</Badge>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Tasks */}
              {selectedEvent.tasks?.length > 0 && (
                <div>
                  <p className="text-slate-500 text-sm mb-2">Tâches</p>
                  <div className="space-y-1">
                    {selectedEvent.tasks.map((task, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                        <span className="w-5 h-5 rounded-full bg-slate-200 text-xs flex items-center justify-center">{i + 1}</span>
                        <span className="text-sm">{task}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Time Tracking Actions */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-medium">Suivi du temps</p>
                    <p className="text-sm text-slate-500">Total enregistré: {getTotalEventHours(selectedEvent.id)}h</p>
                  </div>
                  <div className="flex gap-2">
                    {summary?.active_event_id === selectedEvent.id ? (
                      <Button onClick={() => handleLogTime('stop')} disabled={submitting} className="bg-red-600 hover:bg-red-700">
                        <StopCircle className="w-4 h-4 mr-2" />
                        Arrêter
                      </Button>
                    ) : (
                      <Button onClick={() => handleLogTime('start')} disabled={submitting || summary?.has_active_timer} variant="outline" className="border-emerald-500 text-emerald-600 hover:bg-emerald-50">
                        <PlayCircle className="w-4 h-4 mr-2" />
                        Démarrer
                      </Button>
                    )}
                    <Button onClick={() => setLogDialogOpen(true)} variant="outline">
                      <Plus className="w-4 h-4 mr-2" />
                      Saisie manuelle
                    </Button>
                  </div>
                </div>
                
                {/* Time Logs */}
                {eventLogs.length > 0 && (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    <p className="text-sm font-medium text-slate-600">Historique</p>
                    {eventLogs.map(log => (
                      <div key={log.id} className="flex items-center justify-between p-3 bg-slate-50 rounded text-sm">
                        <div>
                          <span className="font-medium">{log.user_name}</span>
                          <span className="text-slate-400 mx-2">•</span>
                          <span>{log.action === 'manual' ? 'Saisie manuelle' : log.action === 'start' ? 'Démarré' : 'Arrêté'}</span>
                          {log.notes && <p className="text-slate-500 text-xs mt-1">{log.notes}</p>}
                        </div>
                        <div className="text-right">
                          {log.hours > 0 && <Badge variant="secondary">{log.hours}h</Badge>}
                          <p className="text-xs text-slate-400 mt-1">{format(new Date(log.created_at), 'dd/MM HH:mm')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Log Dialog */}
      <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Saisie manuelle du temps</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Heures travaillées *</Label>
              <Input 
                type="number" 
                step="0.25"
                min="0"
                value={logForm.hours}
                onChange={(e) => setLogForm({ ...logForm, hours: e.target.value })}
                placeholder="Ex: 2.5"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={logForm.notes}
                onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })}
                rows={3}
                placeholder="Détails du travail effectué..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLogDialogOpen(false)}>Annuler</Button>
              <Button onClick={() => handleLogTime('manual')} disabled={submitting} className="bg-slate-900 hover:bg-slate-800">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
